import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'
import { connect } from 'react-redux'

import { LoanStep, TermUnit } from 'even-ts-static'

import ThemeConsumer from 'embed/components/ThemeConsumer'
import { AprType, coAppBadgeBreakpoint, PublicClassNames, TRANSUNION_COMPANY_UUID } from 'embed/constants'
import { companyUuid as configCompanyUuid, isDev } from 'embed/constants/config'

import {
  APR_FIXED,
  APR_LABEL,
  APR_POSTFIX,
  APR_VARIABLE,
  EMPTY,
  getAprDisplay,
  getMaxAmountDisplay,
  getMonthlyPaymentDisplay,
  LEGAL,
  LOAN_AMOUNT_PREFIX,
  MONTHLY_PAYMENT_LABEL,
  OPEN_TERM_LABEL,
  OPEN_TERM_LENGTH,
  TERM_LABEL,
} from 'embed/utils/offer-display-utils'

import { getContrastingColor, pluralize } from 'embed/utils'
import { LoanOffer } from 'embed/constants/models'
import { MultiProductState } from 'embed/reducers'

import { shouldDisplayOnLendersSiteLabel } from 'embed/selectors'
import {
  BBBRatingsOnLoanOffers,
  Experiment,
  Original,
  PreviewOfferCtaLanguage,
  ShowAllOffersOnResultsStep,
  Variant,
  withExperiment,
} from 'embed/experiments'

import { CoApplicantOfferBadge } from '../../CoApplicantOfferBadge'
import { Flag } from './flag'

import { BBBRating } from './BBBRating'
const BBBRatingExperiment = withExperiment(BBBRatingsOnLoanOffers)(BBBRating)

const styles = require('embed/styles/results')

interface LoanOfferCtaButtonProps {
  handleClick: (event: React.MouseEvent<Element>) => void
  isResultsView?: boolean
  selectedPreviewOfferCtaLanguageObjectiveComplete?: () => void
  selectedShowAllOffersOnResultsStepComplete?: () => void
}

interface Props {
  aprSuffix?: JSX.Element | string
  bestOfferId?: string
  disclaimer: React.ReactNode
  hideCta: boolean
  isResultsView?: boolean
  monthlyPaymentSuffix?: React.ReactNode
  offer: LoanOffer
  onLoanOfferCtaClick: (event: React.MouseEvent<Element>) => void
  partnerLogo: React.ReactNode
  selectedStep: LoanStep
  setEl: (el: HTMLLIElement) => void
  showOnLenderSiteLabel?: boolean
  shouldDisplayTransunionDisclaimer: boolean
  tooltipText?: string
  trackingPixel: React.ReactNode
  viewportWidth: number
}

const LoanOfferCtaButtonComponent: React.FunctionComponent<LoanOfferCtaButtonProps> = (props) => {
  const onClick = (e) => {
    const {
      handleClick,
      selectedPreviewOfferCtaLanguageObjectiveComplete,
      selectedShowAllOffersOnResultsStepComplete,
      isResultsView,
    } = props
    selectedPreviewOfferCtaLanguageObjectiveComplete?.()
    if (isResultsView) {
      selectedShowAllOffersOnResultsStepComplete?.()
    }
    handleClick(e)
  }
  return (
    <ThemeConsumer>
      {({ borderRadiusSmall, ctaStyles, colorTextPrimary }) => (
        <button
          data-test-id="offer_continue"
          className={classnames(
            styles.button,
            css(`
              color: ${getContrastingColor(ctaStyles?.backgroundColor, { black: colorTextPrimary })};
              border-radius: ${borderRadiusSmall};
              &:hover {opacity: 0.84;}
            `),
          )}
          style={ctaStyles}
          onClick={onClick}
        >
          {props.children}
        </button>
      )}
    </ThemeConsumer>
  )
}

const LoanOfferCtaButton = withExperiment(PreviewOfferCtaLanguage, {
  objectiveCompletePropName: 'selectedPreviewOfferCtaLanguageObjectiveComplete',
})(
  withExperiment(ShowAllOffersOnResultsStep, {
    objectiveCompletePropName: 'selectedShowAllOffersOnResultsStepComplete',
  })(LoanOfferCtaButtonComponent),
)

export const ListItemFull: React.SFC<Props> = ({
  aprSuffix,
  bestOfferId,
  disclaimer,
  hideCta,
  isResultsView,
  monthlyPaymentSuffix,
  offer,
  offer: { productSubType },
  onLoanOfferCtaClick,
  partnerLogo,
  selectedStep,
  setEl,
  showOnLenderSiteLabel,
  shouldDisplayTransunionDisclaimer,
  tooltipText,
  trackingPixel,
  viewportWidth,
}) => {
  const flag = (
    <div className={styles.offerFlagContainer}>
      <Flag
        bestOfferId={bestOfferId}
        offer={offer}
        shouldDisplayTransunionDisclaimer={shouldDisplayTransunionDisclaimer}
        tooltipText={tooltipText}
        viewportWidth={viewportWidth}
        isResultsView={isResultsView}
      />
    </div>
  )
  const paymentAmount = getMonthlyPaymentDisplay(offer)
  const shouldShowOfferAmount = selectedStep === LoanStep.Results && offer.maxAmount
  const showCoApplicantOfferBadge =
    offer.coApplicant && viewportWidth <= coAppBadgeBreakpoint && offer.uuid === bestOfferId
  const coApplicantOfferBadge = showCoApplicantOfferBadge && <CoApplicantOfferBadge />

  const isTransunionSupply: boolean = configCompanyUuid === TRANSUNION_COMPANY_UUID

  let loanOfferTermLabel = TERM_LABEL
  let loanOfferTermLength = `${offer.termLength} ${pluralize(offer.termUnit, offer.termLength)}`

  if (offer.termUnit === TermUnit.Open) {
    loanOfferTermLabel = OPEN_TERM_LABEL
    loanOfferTermLength = OPEN_TERM_LENGTH
  }

  return (
    <ThemeConsumer>
      {({ borderRadiusLarge, colorBorder, colorTextSecondary, fontFamilyPrimary }) => (
        <li
          data-test-val={isDev ? offer.uuid : ''}
          className={classnames(
            PublicClassNames.Offer,
            styles.offer,
            css`
              border-color: ${colorBorder};
              border-radius: ${borderRadiusLarge};
            `,
          )}
          ref={setEl}
        >
          {trackingPixel}
          {flag}
          {coApplicantOfferBadge}
          <div className={classnames(styles.offerWrapper, styles.loanOfferWrapper)}>
            <Experiment experiment={BBBRatingsOnLoanOffers}>
              <Original>
                <div className={classnames(styles.column, styles.originator)}>{partnerLogo}</div>
              </Original>
              <Variant experiment={BBBRatingsOnLoanOffers} variant={BBBRatingsOnLoanOffers.variants.BBBRatings}>
                <div className={classnames(styles.column, styles.originator, css(`text-align: center`))}>
                  {partnerLogo}
                  <BBBRatingExperiment rating={offer.originator?.bbbRating} />
                </div>
              </Variant>
            </Experiment>
            <div className={styles.column}>
              <div
                data-test-id="offer_term"
                className={classnames(
                  styles.stat,
                  css`
                    font-family: ${fontFamilyPrimary};
                  `,
                )}
              >
                {loanOfferTermLength}
              </div>
              <p className={classnames(styles.label, css(`color: ${colorTextSecondary}`))}>
                {loanOfferTermLabel} {offer.originator && offer.originator.key === 'light-stream' ? LEGAL : EMPTY}
              </p>
            </div>
            <div
              className={classnames(
                styles.column,
                css`
                  padding-left: 10px;
                `,
              )}
            >
              <div
                data-test-id="offer_apr"
                className={classnames(
                  styles.stat,
                  css`
                    font-family: ${fontFamilyPrimary};
                  `,
                )}
              >
                {getAprDisplay(offer.meanApr)}
                {APR_POSTFIX}
              </div>
              <p data-test-id="apr_suffix" className={classnames(styles.label, css(`color: ${colorTextSecondary}`))}>
                {offer.aprType && `${offer.aprType === AprType.Variable ? APR_VARIABLE : APR_FIXED} `}
                {APR_LABEL}
                {aprSuffix}
              </p>
            </div>
            <div className={styles.column}>
              <div
                data-test-id="offer_payment"
                className={classnames(
                  styles.stat,
                  css`
                    font-family: ${fontFamilyPrimary};
                  `,
                )}
              >
                {paymentAmount}
              </div>
              {/* Don't show the Monthly Payment label if neither exist */}
              {(offer.meanMonthlyPayment || offer.monthlyPayment) && (
                <p
                  data-test-id="monthly_payment"
                  className={classnames(styles.label, css(`color: ${colorTextSecondary}`))}
                >
                  {MONTHLY_PAYMENT_LABEL}
                  {monthlyPaymentSuffix}
                </p>
              )}
            </div>
            {!hideCta && (
              <div className={classnames(styles.column, styles.cta)}>
                {shouldShowOfferAmount && (
                  <p data-test-id="offer_maxAmount" className={classnames(styles.stat, styles.offerAmount)}>
                    {getMaxAmountDisplay(offer, LOAN_AMOUNT_PREFIX)}
                  </p>
                )}
                <Experiment experiment={PreviewOfferCtaLanguage}>
                  <Original>
                    <LoanOfferCtaButton handleClick={onLoanOfferCtaClick} isResultsView={isResultsView}>
                      Continue
                    </LoanOfferCtaButton>
                  </Original>
                  <Variant
                    experiment={PreviewOfferCtaLanguage}
                    variant={PreviewOfferCtaLanguage.variants.ContinueWithLabel}
                  >
                    <LoanOfferCtaButton handleClick={onLoanOfferCtaClick} isResultsView={isResultsView}>
                      Continue
                    </LoanOfferCtaButton>
                    <p className={classnames(styles.label, styles.ctaHelperText, css(`color: ${colorTextSecondary}`))}>
                      See if you may qualify
                    </p>
                  </Variant>
                  <Variant
                    experiment={PreviewOfferCtaLanguage}
                    variant={PreviewOfferCtaLanguage.variants.SeeIfYouMayQualify}
                  >
                    <LoanOfferCtaButton handleClick={onLoanOfferCtaClick} isResultsView={isResultsView}>
                      See if you may qualify
                    </LoanOfferCtaButton>
                  </Variant>
                  <Variant experiment={PreviewOfferCtaLanguage} variant={PreviewOfferCtaLanguage.variants.CheckMyRates}>
                    <LoanOfferCtaButton handleClick={onLoanOfferCtaClick} isResultsView={isResultsView}>
                      Check My Rates
                    </LoanOfferCtaButton>
                  </Variant>
                  <Variant
                    experiment={PreviewOfferCtaLanguage}
                    variant={PreviewOfferCtaLanguage.variants.CheckMyRatesWithLabel}
                  >
                    <LoanOfferCtaButton handleClick={onLoanOfferCtaClick} isResultsView={isResultsView}>
                      Check My Rates
                    </LoanOfferCtaButton>
                    <p className={classnames(styles.label, styles.ctaHelperText, css(`color: ${colorTextSecondary}`))}>
                      See if you may qualify
                    </p>
                  </Variant>
                  <Variant experiment={PreviewOfferCtaLanguage} variant={PreviewOfferCtaLanguage.variants.GetMatched}>
                    <LoanOfferCtaButton handleClick={onLoanOfferCtaClick} isResultsView={isResultsView}>
                      Get Matched
                    </LoanOfferCtaButton>
                  </Variant>
                </Experiment>
                {showOnLenderSiteLabel && (
                  <div className={classnames(styles.labelOnLendersSite, css(`color: ${colorTextSecondary}`))}>
                    on {offer?.originator?.name}'s site
                  </div>
                )}
                {isTransunionSupply && isResultsView && (
                  <div
                    className={classnames(
                      styles.tuDisclaimer,
                      css`
                        color: ${colorTextSecondary};
                      `,
                    )}
                  >
                    This is not a guaranteed approval
                  </div>
                )}
              </div>
            )}
          </div>
          <div>{disclaimer}</div>
        </li>
      )}
    </ThemeConsumer>
  )
}

const mapStateToProps = (state: MultiProductState) => ({
  showOnLenderSiteLabel: shouldDisplayOnLendersSiteLabel(state),
})

export default connect(mapStateToProps)(ListItemFull)
